import { Injectable } from '@angular/core';
import { AbstractControl, FormGroup, FormArray, FormControl } from '@angular/forms';
import { RuleViolationModel } from '../../../common/models/RuleViolationModel';
import { Subject, BehaviorSubject, Subscription } from 'rxjs';
import { p } from '@angular/core/src/render3';

class AbstractControlContext {
  ruleViolations: RuleViolationModel[] = [];
  subscription: Subscription;
}

@Injectable()
export class RuleVioliationManagerService {
  private _controls: Map<AbstractControl, AbstractControlContext> = new Map<AbstractControl, AbstractControlContext>();
  private _violationMap: Map<RuleViolationModel, AbstractControl[]> = new Map<RuleViolationModel, AbstractControl[]>();
  private _ruleViolations: RuleViolationModel[] = [];
  private _rootForm: FormGroup;

  public rootViolations: RuleViolationModel[] = [];

  ruleViolations$ = new BehaviorSubject<RuleViolationModel[]>([]);
  hasViolations$ = new BehaviorSubject<boolean>(false);

  get ruleViolations(): RuleViolationModel[] {
    return this._ruleViolations;
  }

  set ruleViolations(ruleViolations: RuleViolationModel[]) {
    this._ruleViolations = ruleViolations;
    if (this._rootForm) {
      this._rootForm.updateValueAndValidity();
    }
    this.updateStatus();
  }

  constructor() {}

  public setRootForm(form: FormGroup) {
    this._rootForm = form;
  }

  public registerControl(control: AbstractControl): void {
    var context;
    if (!this._controls.has(control)) {
      context = this.getOrAddFormContext(control);
      context.subscription = control.valueChanges.subscribe(() => {
        if (control.touched) {
          this.clearRuleViolations(control);
        }
      });
    } else {
      context = this.getOrAddFormContext(control);
    }
  }

  public unRegisterControl(control: AbstractControl): void {
    if (this._controls.has(control)) {
      const context = this._controls.get(control);
      if (context.subscription) {
        context.subscription.unsubscribe();
      }
      this.clearRuleViolations(control);
      this._controls.delete(control);
    }
  }

  public setFormGroupValidators(formGroup: FormGroup): void {
    formGroup.validator = (c: AbstractControl): any => {
      this.processRuleViolations();
    };
  }

  public clearAllRuleViolations() {
    this.ruleViolations = [];
    this.rootViolations = [];
    this._controls.forEach((context, control) => {
      this.clearRuleViolations(control, false);
      control.updateValueAndValidity();
    });
  }

  public unRegisterControls(control: FormGroup | FormArray): void {
    Object.keys(control.controls).forEach(key => {
      const childControl = control.get(key);
      if (childControl instanceof FormGroup) {
        this.unRegisterControls(childControl);
      } else if (childControl instanceof FormArray) {
        this.unRegisterControls(childControl);
      } else {
        this.unRegisterControl(childControl);
      }
    });
    this.clearRuleViolations(control);
  }

  public clearRuleViolations(control: AbstractControl, updateStatus: boolean = true): void {
    if (this._controls.has(control)) {
      let violationResolved = false;
      const context = this._controls.get(control);
      for (let i = 0; i < context.ruleViolations.length; i++) {
        const appliedViolation = context.ruleViolations[i];
        const index = this._ruleViolations.indexOf(appliedViolation);
        if (index >= 0) {
          this._ruleViolations.splice(index, 1);
          if (this._violationMap.has(appliedViolation)) {
            const controls = this._violationMap.get(appliedViolation).filter(c => c !== control);
            this._violationMap.delete(appliedViolation);
            controls.forEach(c => {
              c.updateValueAndValidity();
            });
          }
          violationResolved = true;
        }
      }
      context.ruleViolations = [];
      if (violationResolved && updateStatus) {
        this._rootForm.updateValueAndValidity();
        setTimeout(() => {
          this.updateStatus();
        });
      }
    }
  }

  public removeFormGroup(formGroup: any) {
    if (formGroup) {
      Object.keys(formGroup.controls).forEach(key => {
        const currentControl = formGroup.controls[key];
        if (currentControl.controls) {
          this.removeFormGroup(currentControl);
        }
        this.unRegisterControl(currentControl);
      });
    }
  }

  private tryAddErrorToControl(path: string, violation: RuleViolationModel): boolean {
    const control = this._rootForm.get(path);
    if (control) {
      this.addError(control, violation);
      return true;
    } else {
      const index = path.lastIndexOf('.');
      if (index > 0) {
        const parentPath = path.substring(0, index);
        return this.tryAddErrorToControl(parentPath, violation);
      }
    }
    return false;
  }

  public processRuleViolations() {
    if (this._rootForm && this._ruleViolations) {
      this._ruleViolations.forEach(violation => {
        if (violation.propertyNames && violation.propertyNames.length > 0) {
          violation.propertyNames.forEach(propertyName => {
            if (!this.tryAddErrorToControl(propertyName, violation)) {
              this.rootViolations.push(violation);
            }
          });
        } else {
          if (!this.rootViolations.includes(violation)) {
            this.rootViolations.push(violation);
          }
        }
      });
    }
  }

  private addControlToViolation(control: AbstractControl, violation: RuleViolationModel) {
    let controls: AbstractControl[] = [];
    if (this._violationMap.has(violation)) {
      controls = this._violationMap.get(violation);
    } else {
      this._violationMap.set(violation, controls);
    }
    if (controls.indexOf(control) === -1) {
      controls.push(control);
    }
  }

  private addError(control: AbstractControl, violation: RuleViolationModel) {
    var context = this.getOrAddFormContext(control as AbstractControl);
    var errors = violation;
    var currentErrors = control.getError('rule-violation');
    if (currentErrors) {
      if (!Array.isArray(currentErrors)) {
        currentErrors = [currentErrors];
      }
      if (currentErrors.indexOf(violation) === -1) {
        currentErrors.push(errors);
        if (context.ruleViolations.find(v => v === violation) === undefined) {
          context.ruleViolations.push(violation);
          this.addControlToViolation(control, violation);
        }
      }
      errors = currentErrors;
    } else if (context.ruleViolations.find(v => v === violation) === undefined) {
      context.ruleViolations.push(violation);
      this.addControlToViolation(control, violation);
    }
    control.setErrors({ 'rule-violation': errors });
    control.markAsDirty();
    if (control instanceof FormControl) {
      control.markAsTouched();
    }
  }

  public getRuleViolations(control: AbstractControl): RuleViolationModel[] {
    if (!this._controls.has(control)) {
      return [];
    }
    return this._controls.get(control).ruleViolations;
  }

  public controlHasViolations(control: AbstractControl): boolean {
    if (!this._controls.has(control)) {
      return false;
    }
    var context = this._controls.get(control);
    return context.ruleViolations && context.ruleViolations.length > 0;
  }

  private getOrAddFormContext(control: AbstractControl): AbstractControlContext {
    if (!this._controls.has(control)) {
      this._controls.set(control, new AbstractControlContext());
    }
    return this._controls.get(control);
  }

  private updateStatus() {
    this.ruleViolations$.next(this._ruleViolations);
    this.hasViolations$.next(this._ruleViolations && this._ruleViolations.length > 0);
  }
}



// WEBPACK FOOTER //
// ./src/app/pillar/workspace/services/rule-violiation-manager.service.ts